// src/invoices/index.js — екран „Фактури" (Фаза 3.2, последният мигриран).

import { el, clear, $, clickableRow } from '../shared/dom.js';
import { money, dfmt } from '../shared/format.js';
import { toast } from '../shared/toast.js';
import { api } from '../shared/api.js';
import { emptyState, actionsMenu, searchInput, docNo, dominantStatus, statusCell, sentStatusCell, matchesStatus, RECEIVED_OPTION, invoiceOverdue, periodGroup } from '../shared/ui.js';
import { bulkManager } from '../shared/pickers.js';
import { confirmDialog } from '../shared/modal.js';
import { state, cq, activeCompanyObj, companyBooks, contacts as contactsCache, invoicesCache, setInvoices, canEditPerm } from '../shared/state.js';
import { dateInput, localTodayIso } from '../shared/dates.js';
import { invoiceForm } from './form.js';
import { printInvoicesTable } from './print.js';
import { exportKonto6, exportInvoicesDialog, importInvoicesXlsx, downloadInvoiceTemplate } from './xlsx.js';
import { kontoSettingsDialog } from '../shared/kontoSettings.js';
import { exportRunsDialog } from '../shared/kontoExport.js';
import { sortTh, sortLabel } from '../shared/sortHeader.js';
import { regHead, regFilters, regField, regCount } from '../shared/register.js';

export const INV_STATUS = { draft: ['Чернова', 'gray'], sent: ['Изпратена', 'blue'], paid: ['Платена', 'green'], overdue: ['Просрочена', 'red'] };
// Кантората = администратор и потребител; клиентът е фирмата-клиент.
const isStaff = () => { const u = state().user; return !!u && u.role !== 'client'; };

export async function view(page) {
  clear(page);
  if (!state().activeCompany) return window.CRM.needsCompany(page);
  let invoices = [], contacts = [];
  // Контактите са помощни: отказът им (напр. без право) не бива да изпразва
  // самия списък — грешка от картата, 16.10.2026 (тогава Promise.all падаше
  // целият и екранът казваше „Още няма …").
  const [invR, conR] = await Promise.allSettled([api('/invoices' + cq()), api('/contacts' + cq())]);
  if (invR.status === 'fulfilled') invoices = invR.value || []; else toast(invR.reason.message, true);
  if (conR.status === 'fulfilled') contacts = conR.value || [];
  setInvoices(invoices);
  const cName = (id) => { const c = contacts.find((x) => x.id === id); return c ? c.name : '—'; };

  // Филтрите и помощниците се дефинират ПРЕДИ лентата с бутони: действията в
  // менюто („Табличен изглед", „Експорт за Конто 6") ги ползват, а при празен
  // списък по-долу има ранен изход — иначе кликът гърми с TDZ грешка.
  const flt = { q: '', status: '', year: '', book: '', from: '', to: '', sortKey: 'date', sortDir: 'desc' };
  let lastRows = [];
  const _bks = companyBooks(activeCompanyObj() || {});
  const numOf = (i) => parseInt(String(i.number || '').replace(/\D/g, ''), 10) || 0;
  const bookOf = (i) => { const d = numOf(i); for (let k = 0; k < _bks.length; k++) { const e = (k + 1 < _bks.length) ? _bks[k + 1].start : Infinity; if (d >= _bks[k].start && d < e) return _bks[k].name; } return ''; };
  const bookRange = (start) => { const idx = _bks.findIndex((b) => String(b.start) === String(start)); const e = (idx >= 0 && idx + 1 < _bks.length) ? _bks[idx + 1].start : Infinity; return [Number(start), e]; };

  // „Само преглед" (ОТЧЕТ-ПРОМТ-38, Д3): без право за промяна бутоните,
  // които създават или променят фактури, ги няма — иначе човекът попълва
  // цялата форма и чак на „Запази" разбира, че не може. Отварянето на
  // фактура за преглед, печатът и износът към Excel остават.
  const canEdit = canEditPerm('invoices');
  const panel = el('div', { class: 'panel invoice-register' });
  const bulk = bulkManager({ endpoint: '/invoices', refresh: () => window.CRM.renderView($('#page')), label: 'фактури', statuses: INV_STATUS });
  // Видът е като в „Доставки" (Макс, 29.10.2026): заглавието вляво, честите
  // бутони вдясно на един ред, основният — последен. Рядко ползваните са в
  // „Още ▾", за да не стане редът твърде дълъг; функциите са същите.
  const konto = isStaff() ? [
    // Свалените досега файлове („Свали същия файл") — и за фирма без склад.
    el('button', { class: 'btn sm ghost', type: 'button', text: 'Файлове за Конто', onClick: () => exportRunsDialog() }),
    // Всичко, което файлът за Конто пише сам — до самия експорт (т. 3г).
    el('button', { class: 'btn sm ghost', type: 'button', text: 'Настройки за Конто…', onClick: () => kontoSettingsDialog(activeCompanyObj()) }),
    // Филтрите отиват НА СЪРВЪРА: той избира редовете по същите правила,
    // заключва ги в същата заявка и ги връща за Excel-а (виж xlsx.js).
    // Само за кантората (Промени 16.10, т. 3а): експортът заключва
    // фактурите — клиентът не го вижда, а и сървърът му отказва.
    // Експортът заключва фактурите — това е промяна, затова и правото за нея.
    canEdit ? el('button', { class: 'btn sm ghost', type: 'button', text: 'Експорт за Конто 6', onClick: () => exportKonto6({ status: flt.status, year: flt.year, from: flt.from, to: flt.to, book: flt.book, q: flt.q }, contacts, invoices) }) : null,
  ] : [];
  const more = actionsMenu('Още', [
      // (Б.3) Издаването взима истински номера от брояча и не се връща —
      // затова първо преглед (същото сървърно правило) и въпрос с броя.
      // Скрито, докато фирмата няма нито една повтаряща се фактура (Промени
      // 16.10, т. 3в: „не се ползва"). Заварената си остава видима и
      // управляема — иначе не би имало как да се издаде или изключи.
      (canEdit && invoices.some((i) => i.recurring)) ? { label: 'Издай повтарящите се фактури', onClick: async () => {
        try {
          const prev = await api('/invoices/generate-due/preview' + cq());
          if (!prev.count) { toast('Няма фактури за издаване днес.'); return; }
          const cname = (activeCompanyObj() || {}).name || '';
          const q = prev.count === 1
            ? 'Ще бъде издадена 1 фактура за „' + cname + '". Да продължа ли?'
            : 'Ще бъдат издадени ' + prev.count + ' фактури за „' + cname + '". Да продължа ли?';
          if (!(await confirmDialog(q, { okText: 'Издай' }))) return;
          const r = await api('/invoices/generate-due' + cq(), { method: 'POST' });
          toast('Издадени: ' + r.created + '.');
          // Пропуснатите (склад „Забранявай" и недостиг) се казват изрично —
          // иначе „Издадени: 0" изглежда като „нямаше какво".
          if (Array.isArray(r.skipped) && r.skipped.length) toast(r.skipped.map((x) => '№ ' + x.number + ': ' + x.text).join(' '), true);
          window.CRM.renderView($('#page'));
        }
        catch (e) { toast(e.message, true); } } } : null,
      canEdit ? { label: 'Импорт на фактури от Excel', onClick: () => importInvoicesXlsx(contacts) } : null,
      canEdit ? { label: 'Свали празен Excel образец', onClick: () => downloadInvoiceTemplate() } : null,
      { label: 'Експорт към Excel', onClick: () => exportInvoicesDialog(invoices, contacts) },
      { label: 'Печат на списъка', onClick: async () => {
        const ids = bulk.selectedIds ? bulk.selectedIds() : [];
        let list, title;
        if (ids.length) {
          const set = new Set(ids.map(String));
          list = invoices.filter((i) => set.has(String(i.id)));
          title = 'Избрани фактури';
        } else if (flt.from || flt.to || flt.year || flt.status || flt.q || flt.book) {
          list = lastRows.slice();
          title = periodTitle();
        } else {
          const n = new Date(), y = n.getFullYear(), m = n.getMonth(), p2 = (x) => ('0' + x).slice(-2);
          const f = y + '-' + p2(m + 1) + '-01', t = y + '-' + p2(m + 1) + '-' + p2(new Date(y, m + 1, 0).getDate());
          list = invoices.filter((i) => { const d = (i.issue_date || '').slice(0, 10); return d >= f && d <= t; });
          const MN = ['януари', 'февруари', 'март', 'април', 'май', 'юни', 'юли', 'август', 'септември', 'октомври', 'ноември', 'декември'];
          title = 'Фактури за ' + MN[m] + ' ' + y;
        }
        if (!list.length) { toast('Няма фактури за печат при този избор.', true); return; }
        // Казваме какво точно ще излезе — изборът иначе е невидим (В.2).
        const src = ids.length ? 'избраните с отметка' : ((flt.from || flt.to || flt.year || flt.status || flt.q || flt.book) ? 'по текущия филтър' : 'текущият месец');
        if (!(await confirmDialog('Ще се отпечатат: ' + title.toLowerCase() + ' (' + list.length + ' бр.) — ' + src + '.', { okText: 'Печат' }))) return;
        printInvoicesTable(list, cName, title);
      } },
    ]);
  const head = regHead('Фактури', [...konto, more],
    canEdit ? el('button', { class: 'btn sm', type: 'button', text: '+ Нова фактура', onClick: () => invoiceForm(null, contacts) }) : null,
    bulk.toolbar);
  panel.appendChild(head);
  if (!invoices.length) { panel.appendChild(canEdit ? emptyState('⎙', 'Още няма фактури.', '+ Нова фактура', () => invoiceForm(null, contacts)) : emptyState('⎙', 'Още няма фактури.')); page.appendChild(panel); return; }

  // --- Филтри + сортиране ---
  function periodTitle() {
    const MN = ['януари', 'февруари', 'март', 'април', 'май', 'юни', 'юли', 'август', 'септември', 'октомври', 'ноември', 'декември'];
    if (flt.from && flt.to) {
      const f = flt.from.slice(0, 7), t = flt.to.slice(0, 7);
      if (f === t) { const p = f.split('-'); return 'Фактури за ' + (MN[Number(p[1]) - 1] || p[1]) + ' ' + p[0]; }
      return 'Фактури ' + dfmt(flt.from) + ' – ' + dfmt(flt.to);
    }
    if (flt.year) return 'Фактури за ' + flt.year;
    return 'Фактури';
  }
  const years = [...new Set(invoices.map((i) => (i.issue_date || '').slice(0, 4)).filter(Boolean))].sort().reverse();
  const search = searchInput({ placeholder: 'Търси № или клиент…', ariaLabel: 'Търсене на фактура по номер или клиент', onSearch: (q) => { flt.q = q; draw(); } });
  const statusSel = el('select', { onChange: (e) => { flt.status = e.target.value; draw(); } }, el('option', { value: '', text: 'Всички статуси' }), ...Object.keys(INV_STATUS).map((k) => el('option', { value: k, text: INV_STATUS[k][0] })),
    el('option', { value: RECEIVED_OPTION[0], text: RECEIVED_OPTION[1] }));
  const yearSel = el('select', { onChange: (e) => { flt.year = e.target.value; draw(); } }, el('option', { value: '', text: 'Всички години' }), ...years.map((y) => el('option', { value: y, text: y })));
  // dateInput, не native <input type=date>: последният се изписва по езика
  // на браузъра (mm/dd/yyyy на английски) — риск от объркан период.
  const fromI = dateInput(flt.from || '');
  fromI.title = 'От дата (период)'; fromI.setAttribute('aria-label', 'От дата (период)');
  fromI.addEventListener('input', () => { const v = fromI.dataset.iso; if (v !== flt.from) { flt.from = v; draw(); } });
  const toI = dateInput(flt.to || '');
  toI.title = 'До дата (период)'; toI.setAttribute('aria-label', 'До дата (период)');
  toI.addEventListener('input', () => { const v = toI.dataset.iso; if (v !== flt.to) { flt.to = v; draw(); } });
  const period = periodGroup(fromI, toI);
  // Филтрите с надписи отгоре, като в „Доставки"; броячът — над таблицата.
  const bookSel = _bks.length > 1 ? el('select', { onChange: (e) => { flt.book = e.target.value; draw(); } }, el('option', { value: '', text: 'Всички фактурници' }), ..._bks.map((b) => el('option', { value: b.start, text: b.name }))) : null;
  const filterBar = regFilters(
    regField('№ или клиент', search, 'l'),
    regField('Статус', statusSel),
    regField('Година', yearSel),
    regField('От дата – до дата', period, 'l'),
    bookSel ? regField('Фактурник', bookSel) : null);
  panel.appendChild(filterBar);
  const countLbl = regCount();
  panel.appendChild(countLbl);

  function setSort(k) { if (flt.sortKey === k) flt.sortDir = flt.sortDir === 'asc' ? 'desc' : 'asc'; else { flt.sortKey = k; flt.sortDir = 'asc'; } draw(); }
  const thNo = sortTh({}, () => setSort('number'));
  const thCl = sortTh({}, () => setSort('client'));
  const thDt = sortTh({ class: 'hide-sm' }, () => setSort('date'));
  const thSm = sortTh({ class: 'num' }, () => setSort('total'));
  const thSt = sortTh({}, () => setSort('status'));
  // Преобладаващият статус се приглушава, различаващите се получават цветно
  // етикетче. Колоната ОСТАВА видима винаги — виж dominantStatus/statusCell.
  const _stDominant = dominantStatus(invoices);
  const tb = el('tbody');
  function draw() {
    bulk.reset();
    clear(tb);
    const _today = localTodayIso();
    let rows = invoices.filter((i) => {
      // Филтърът „Просрочена" хваща и ИЗЧИСЛЕНИТЕ просрочени (минал падеж),
      // не само ръчно маркираните — иначе Таблото брои едно, филтърът друго.
      if (flt.status) {
        const hit = flt.status === 'overdue'
          ? (i.status === 'overdue' || invoiceOverdue(i, _today))
          : matchesStatus(i, flt.status);
        if (!hit) return false;
      }
      if (flt.year && (i.issue_date || '').slice(0, 4) !== flt.year) return false;
      if (flt.from && (i.issue_date || '').slice(0, 10) < flt.from) return false;
      if (flt.to && (i.issue_date || '').slice(0, 10) > flt.to) return false;
      if (flt.book) { const [s, e] = bookRange(flt.book); const d = numOf(i); if (!(d >= s && d < e)) return false; }
      if (flt.q) { const hay = (i.number + ' ' + cName(i.contact_id) + ' ' + (i.notes || '')).toLowerCase(); if (!hay.includes(flt.q)) return false; }
      return true;
    });
    const dir = flt.sortDir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      if (flt.sortKey === 'client') { const x = cName(a.contact_id).toLowerCase(), y = cName(b.contact_id).toLowerCase(); return x < y ? -dir : x > y ? dir : 0; }
      // При равни дати — по номер: иначе фактурите от един ден остават в
      // реда от базата (ред на вмъкване) и номерата изглеждат разбъркани.
      if (flt.sortKey === 'date') { const x = a.issue_date || '', y = b.issue_date || ''; if (x !== y) return x < y ? -dir : dir; return (numOf(a) - numOf(b)) * dir; }
      if (flt.sortKey === 'total') return ((Number(a.total) || 0) - (Number(b.total) || 0)) * dir;
      if (flt.sortKey === 'status') { const x = a.status || '', y = b.status || ''; return x < y ? -dir : x > y ? dir : 0; }
      return (numOf(a) - numOf(b)) * dir;
    });
    lastRows = rows;
    let _lastYM = null;
    const _grp = (flt.sortKey === 'number' || flt.sortKey === 'date');
    const _MN = ['януари', 'февруари', 'март', 'април', 'май', 'юни', 'юли', 'август', 'септември', 'октомври', 'ноември', 'декември'];
    rows.forEach((inv) => {
      if (_grp) { const _ym = (inv.issue_date || '').slice(0, 7); if (_ym && _ym !== _lastYM) { _lastYM = _ym; const _p = _ym.split('-'); tb.appendChild(el('tr', { class: 'month-row' }, el('td', { class: 'month-sep', colspan: 6 }, (_MN[Number(_p[1]) - 1] || _p[1]) + ' ' + _p[0]))); } }
      // Просрочената (по правилото на Таблото) личи ВИНАГИ — червен етикет,
      // никога приглушен от dominantStatus. Ръчният статус 'overdue' си
      // минава по статусната карта (същият червен етикет).
      const stCell = (inv.status !== 'overdue' && invoiceOverdue(inv, _today))
        ? el('td', {}, el('span', { class: 'tag red', text: 'Просрочена', title: 'Падежът е минал: ' + dfmt(inv.due_date || inv.issue_date) }))
        : sentStatusCell(inv, _stDominant, INV_STATUS);
      // Катинарът казва И защо е заключена (подсказка + име за четеца).
      const lockMark = inv.konto_exported_at
        ? el('span', { text: ' 🔒', title: 'Заключена при експорт към Конто на ' + dfmt(inv.konto_exported_at), 'aria-label': 'Заключена при експорт към Конто на ' + dfmt(inv.konto_exported_at) })
        : null;
      tb.appendChild(clickableRow(() => invoiceForm(inv, contacts), 'Отвори фактура № ' + inv.number + (inv.konto_exported_at ? ' (заключена)' : ''),
        bulk.cell(inv.id, 'фактура № ' + inv.number),
        el('td', { style: 'font-weight:600' }, '№ ', docNo(inv.number), (inv.recurring ? ' ↻' : ''), lockMark,
          // Авансовата личи в списъка — от нея се приспада в окончателната.
          inv.advance ? el('span', { class: 'tag teal', style: 'margin-left:6px', text: 'аванс' }) : null,
          (inv.recurring && inv.recurring_skipped && inv.recurring_skipped.text) ? el('div', {}, el('span', { class: 'tag amber', text: inv.recurring_skipped.text })) : null, _bks.length > 1 ? el('div', { style: 'font-weight:400;font-size:11px;color:var(--muted)', text: bookOf(inv) }) : null),
        el('td', {}, cName(inv.contact_id)),
        el('td', { class: 'hide-sm' }, dfmt(inv.issue_date)),
        el('td', { class: 'num' }, money(inv.total, inv.currency)),
        stCell
      ));
    });
    sortLabel(thNo, '№', flt.sortKey === 'number' ? flt.sortDir : ''); sortLabel(thCl, 'Клиент', flt.sortKey === 'client' ? flt.sortDir : ''); sortLabel(thDt, 'Дата', flt.sortKey === 'date' ? flt.sortDir : ''); sortLabel(thSm, 'Сума', flt.sortKey === 'total' ? flt.sortDir : ''); sortLabel(thSt, 'Статус', flt.sortKey === 'status' ? flt.sortDir : '');
    countLbl.set(rows.length, invoices.length, flt.q);
    syncFilterNote(rows.length);
  }

  // (Г.6) „Фактурите ми изчезнаха": празната таблица при активен филтър
  // вече обяснява и дава изход. Бутонът стои при ВСЕКИ активен филтър —
  // напомня, че списъкът е стеснен, дори когато има резултати.
  const filterActive = () => !!(flt.q || flt.status || flt.year || flt.from || flt.to || flt.book);
  const emptyNote = el('div', { class: 'filter-empty', role: 'status' },
    el('p', { style: 'margin:0 0 10px', text: 'Няма фактури по избраните филтри.' }));
  const clearAllBtn = el('button', { class: 'btn sm ghost', text: 'Изчисти всички филтри', onClick: () => {
    flt.q = ''; flt.status = ''; flt.year = ''; flt.from = ''; flt.to = ''; flt.book = '';
    // Нулират се и ПОЛЕТАТА — иначе екранът показва всичко, а филтрите
    // визуално стоят „включени" и объркват двойно.
    if (search.inputEl) search.inputEl.value = '';
    statusSel.value = ''; yearSel.value = '';
    fromI.value = ''; fromI.dataset.iso = ''; toI.value = ''; toI.dataset.iso = '';
    [...filterBar.querySelectorAll('select')].forEach((x) => { if (![statusSel, yearSel].includes(x)) x.value = ''; });
    draw();
  } });
  function syncFilterNote(shown) {
    emptyNote.style.display = (filterActive() && !shown) ? '' : 'none';
    clearAllBtn.style.display = filterActive() ? '' : 'none';
  }
  // (Г.4) На тесен екран редът става карта (.cards-sm/.inv-cards в CSS) —
  // сумата и статусът не се губят зад хоризонтален скрол.
  filterBar.appendChild(clearAllBtn);
  panel.appendChild(el('table', { class: 'cards-sm inv-cards reg-list' }, el('thead', {}, el('tr', {}, bulk.headTh, thNo, thCl, thDt, thSm, thSt)), tb));
  panel.appendChild(emptyNote);
  draw();
  page.appendChild(panel);
}
